import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link, useOutletContext } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Image } from '@/components/ui/image';
import { Play, Clock, ArrowLeft, Calendar, Radio } from 'lucide-react';
import OnAirPill from '@/components/yakaleo/OnAirPill';
import InlineLoader from '@/components/yakaleo/InlineLoader';
import HostSocials from '@/components/yakaleo/HostSocials';
import { formatTime, findOnAirShow } from '@/lib/schedule';
import ShowCountdown from '@/components/yakaleo/ShowCountdown';
import AddToCalendar from '@/components/yakaleo/AddToCalendar';
import Tracklist from '@/components/yakaleo/Tracklist';
import Replays from '@/components/yakaleo/Replays';
import { useSeo, useNoindexIf, useJsonLd } from '@/lib/seo';
import { blurSrc } from '@/lib/artwork';

const STATION_LOGO = 'https://media.base44.com/images/public/6a7a29e52fd1e2fde519cec0/6883675ae_opt_art.jpg';

const DAY_NAMES = {
  es: {
    monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles',
    thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo',
  },
  en: {
    monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
    thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
  },
};

function formatDaysFull(days, lang) {
  if (!days || !days.length) return '';
  const order = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const sorted = [...days].sort((a, b) => order.indexOf(a) - order.indexOf(b));
  const names = DAY_NAMES[lang];
  const weekdaySet = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  if (sorted.length === 5 && weekdaySet.every((d) => sorted.includes(d))) {
    return lang === 'es' ? 'Lunes a Viernes' : 'Monday to Friday';
  }
  return sorted.map((d) => names[d]).join(', ');
}

export default function ShowDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const { setIsPlaying, liveData } = useOutletContext();
  const es = location.pathname.startsWith('/programa');
  const lang = es ? 'es' : 'en';
  const [show, setShow] = useState(null);
  const [host, setHost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    setShow(null);
    setHost(null);
    base44.entities.Show.filter({ slug }, '-created_date', 1)
      .then(async (res) => {
        if (!active) return;
        const s = res && res[0];
        if (!s) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setShow(s);
        if (s.host_id) {
          const h = await base44.entities.Host.get(s.host_id).catch(() => null);
          if (active) setHost(h);
        }
      })
      .catch(() => {
        if (active) setNotFound(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  useSeo({
    canonicalPath: es ? `/programa/${slug}` : `/en/show/${slug}`,
    lang,
    twinPath: es ? `/en/show/${slug}` : `/programa/${slug}`,
    title: show ? (es ? `${show.name} | Yakaleo` : `${show.name} · ${formatDaysFull(show.days, 'en')} | Yakaleo`) : undefined,
    description: show ? (lang === 'es' ? show.description : show.description_en || show.description) : undefined,
    image: show?.artwork_url,
  });
  useNoindexIf(notFound);

  useJsonLd('show-jsonld', show ? {
    '@context': 'https://schema.org',
    '@type': 'RadioSeries',
    name: show.name,
    description: es ? show.description : (show.description_en || show.description),
    image: show.artwork_url,
    inLanguage: lang,
    url: `https://yakaleo.com${es ? `/programa/${slug}` : `/en/show/${slug}`}`,
  } : null);

  if (loading) return <InlineLoader className="min-h-[60vh]" />;

  if (notFound || !show) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-20 text-center">
        <p className="text-neutral-400 text-lg">{es ? 'Programa no encontrado.' : 'Show not found.'}</p>
        <Link to={es ? '/programacion' : '/en/schedule'} className="text-purple-700 text-sm font-bold hover:underline mt-4 inline-block">
          ← {es ? 'Volver a la programación' : 'Back to Schedule'}
        </Link>
      </div>
    );
  }

  const description = lang === 'es' ? show.description : (show.description_en || show.description);
  const hostBio = host ? (lang === 'es' ? host.bio_short_es : host.bio_short) : null;
  const isLive = !!findOnAirShow([show], liveData);

  return (
    <div className="bg-neutral-100 min-h-screen">
      {/* Hero */}
      <div className="relative overflow-hidden bg-black">
        {show.artwork_url && (
          <img src={blurSrc(show.artwork_url)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-30 blur-2xl scale-125" />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-purple-950/60 to-black/90" />
        <div className="relative max-w-[1100px] mx-auto px-4 md:px-8 py-6 md:py-10">
          <Link
            to={es ? '/programacion' : '/en/schedule'}
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-semibold"
          >
            <ArrowLeft size={16} /> {es ? 'Volver a la programación' : 'Back to Schedule'}
          </Link>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-[minmax(0,360px)_1fr] gap-6 md:gap-10 items-center">
            {/* Square artwork — shown full (the host's face sits at the top,
                the show name at the bottom). Falls back to the station logo
                when the show has no artwork. */}
            <div className="w-full max-w-[360px] mx-auto md:mx-0">
              <div className="aspect-square rounded-2xl overflow-hidden bg-neutral-800 shadow-2xl ring-1 ring-white/10">
                <Image
                  src={show.artwork_url || STATION_LOGO}
                  alt={show.name}
                  className="w-full h-full object-cover object-center"
                  fittingType="fill"
                />
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                {isLive ? (
                  <OnAirPill es={es} />
                ) : (
                  <span className="inline-flex items-center gap-2 text-white/70 text-xs font-bold tracking-[0.25em]">
                    <Radio size={14} className="text-fuchsia-300" /> {es ? 'PROGRAMA' : 'SHOW'}
                  </span>
                )}
                <ShowCountdown show={show} isLive={isLive} />
              </div>
              <h1 className="text-white text-3xl md:text-5xl font-black tracking-tight leading-tight">{show.name}</h1>
              {host && (
                <Link to={`/hosts/${host.id}`} className="inline-block mt-2 text-lg md:text-xl font-bold hover:opacity-80 transition-opacity">
                  <span className="text-fuchsia-300">{es ? 'con' : 'with'}</span> <span className="text-white">{host.name}</span>
                </Link>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-white text-sm font-semibold">
                  <Calendar size={14} className="text-fuchsia-300" />
                  {formatDaysFull(show.days, lang)}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-white text-sm font-semibold">
                  <Clock size={14} className="text-fuchsia-300" />
                  {formatTime(show.start_time)} – {formatTime(show.end_time)} ET
                </span>
              </div>

              {description && (
                <p className="text-white/75 text-base md:text-lg mt-5 leading-relaxed max-w-2xl">{description}</p>
              )}

              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => setIsPlaying(true)}
                  className="inline-flex items-center gap-2.5 px-7 py-3 md:px-9 md:py-3.5 bg-purple-600 text-white font-bold tracking-[0.2em] text-xs md:text-sm rounded-full hover:bg-purple-500 transition-colors shadow-lg shadow-purple-900/40"
                >
                  <Play size={16} fill="currentColor" />
                  {es ? 'ESCUCHAR EN VIVO' : 'LISTEN LIVE'}
                </button>
                <div className="bg-white rounded-full px-4">
                  <AddToCalendar show={show} hostName={host?.name} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-6 md:py-10">
        {host && (
          <div className="bg-white rounded-2xl shadow-sm p-5 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="text-xs font-black tracking-[0.2em] text-purple-700 uppercase">{es ? 'Presentado por' : 'Hosted by'}</span>
              <span className="flex-1 h-px bg-gradient-to-r from-neutral-300 to-transparent" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-6 items-start">
              <Link to={`/hosts/${host.id}`} className="block w-full max-w-[160px] mx-auto sm:mx-0">
                <div className="aspect-square rounded-full overflow-hidden bg-neutral-100 ring-4 ring-purple-100">
                  {host.photo_url ? (
                    <Image src={host.photo_url} alt={host.name} className="w-full h-full object-cover object-center" fittingType="fill" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-700 to-black">
                      <span className="text-white text-5xl font-black">{(host.name || '?').charAt(0)}</span>
                    </div>
                  )}
                </div>
              </Link>
              <div className="text-center sm:text-left">
                <Link
                  to={`/hosts/${host.id}`}
                  className="text-black text-2xl font-black hover:text-purple-700 transition-colors"
                >
                  {host.name}
                </Link>
                {host.role && (
                  <p className="text-purple-700 text-xs font-bold tracking-widest uppercase mt-1">{host.role}</p>
                )}
                {hostBio && (
                  <p className="text-neutral-600 text-base mt-3 leading-relaxed whitespace-pre-line">{hostBio}</p>
                )}
                <div className="mt-4 flex flex-wrap items-center justify-center sm:justify-start gap-4">
                  <Link
                    to={`/hosts/${host.id}`}
                    className="inline-flex items-center rounded-full bg-purple-50 text-purple-700 px-4 py-2 text-sm font-bold hover:bg-purple-100 transition-colors"
                  >
                    {es ? 'Ver biografía completa' : 'Read full bio'} →
                  </Link>
                  <HostSocials host={host} size={20} />
                </div>
              </div>
            </div>
          </div>
        )}

        <Tracklist show={show} isLive={isLive} />
        <Replays mixcloudFeed={show.mixcloud_feed} />
      </div>
    </div>
  );
}